import { AIRPORTS, FLIGHT_TYPES, TERMINALS } from '../constants/area';
import {
  BUSINESS_START_HOUR,
  BUSINESS_START_MINUTE,
  PRIVATE_ROOM_BASE_DATE_BOUNDARY_HOUR,
  SHOWER_BASE_DATE_BOUNDARY_HOUR,
} from '../constants/reservation';
import { SPACE_TYPE } from '../constants/space';

/**
 * JSTカレンダー日付を表す擬似Date型。
 * 実体は「JSTカレンダー日 + ローカルTZ 00:00:00」のDateだが、
 * `.toISOString()` / `.getTime()` / `.valueOf()` を呼ぶとUTC時刻が返って
 * カレンダー日と乖離するため、これらを型から除外する。
 *
 * 比較・算術は本ファイルの `compareCalendarDate` / `addCalendarDays` 等を、
 * 文字列化は `getJstDateString` または `toDateString` を使う。
 */
export type JstCalendarDate = Pick<
  Date,
  'getFullYear' | 'getMonth' | 'getDate' | 'getDay'
>;

/**
 * Y/M/D だけ読めれば良いユーティリティの引数型。
 * `Date` も `JstCalendarDate` も代入可能。
 */
export type CalendarDateLike = Pick<
  Date,
  'getFullYear' | 'getMonth' | 'getDate'
>;

/**
 * JSTカレンダー日マーカーを生成する。
 *
 * @param month 1始まり（1〜12が通常範囲）。
 *   範囲外（0 / 13 等）や day の範囲外（0 / 32 等）は JS Date 仕様と同様にロールオーバーする。
 *   例: month=0 → 前年12月、month=13 → 翌年1月、day=0 → 前月末日。
 *   この挙動はカレンダー生成（前月末セル等）で意図的に利用している。
 */
export const createJstCalendarDate = (
  year: number,
  month: number,
  day: number,
): JstCalendarDate => new Date(year, month - 1, day, 0, 0, 0, 0);

/**
 * カレンダー日付に日数を加算した JstCalendarDate を返す。
 */
export const addCalendarDays = (
  base: CalendarDateLike,
  days: number,
): JstCalendarDate =>
  new Date(
    base.getFullYear(),
    base.getMonth(),
    base.getDate() + days,
    0,
    0,
    0,
    0,
  );

/**
 * 指定カレンダー日と同じ年月の1日を表す JstCalendarDate を返す。
 */
export const firstDayOfMonth = (base: CalendarDateLike): JstCalendarDate =>
  new Date(base.getFullYear(), base.getMonth(), 1, 0, 0, 0, 0);

/**
 * 指定カレンダー日から months ヶ月ずらした「月初」の JstCalendarDate を返す。
 * 月送り処理で月末日のロールバック問題を避けるため、常に1日に揃える。
 */
export const addMonthsKeepingDayOne = (
  base: CalendarDateLike,
  months: number,
): JstCalendarDate =>
  new Date(base.getFullYear(), base.getMonth() + months, 1, 0, 0, 0, 0);

const toCalendarOrdinal = (d: CalendarDateLike): number =>
  d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();

/**
 * カレンダー日を Y/M/D で比較する。
 * a < b なら -1、a > b なら 1、同日なら 0。
 */
export const compareCalendarDate = (
  a: CalendarDateLike,
  b: CalendarDateLike,
): -1 | 0 | 1 => {
  const av = toCalendarOrdinal(a);
  const bv = toCalendarOrdinal(b);
  if (av < bv) return -1;
  if (av > bv) return 1;
  return 0;
};

export const isSameCalendarDate = (
  a: CalendarDateLike,
  b: CalendarDateLike,
): boolean => compareCalendarDate(a, b) === 0;

export const isBeforeCalendarDate = (
  a: CalendarDateLike,
  b: CalendarDateLike,
): boolean => compareCalendarDate(a, b) < 0;

export const isAfterCalendarDate = (
  a: CalendarDateLike,
  b: CalendarDateLike,
): boolean => compareCalendarDate(a, b) > 0;

/**
 * 指定した Date の JST 構成要素を返す内部ヘルパー
 * Intl.DateTimeFormat + timeZone:'Asia/Tokyo' を使用するため、
 * 端末のタイムゾーン設定に依存しない。
 */
type JstDatePartType = 'year' | 'month' | 'day' | 'hour' | 'minute';

export const getJstDateInfo = (date: Date) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  }).formatToParts(date);
  const get = (type: JstDatePartType) =>
    parseInt(parts.find((p) => p.type === type)?.value ?? '0', 10);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    // hour12:false 環境によっては '24' が返るため % 24 で正規化
    hour: get('hour') % 24,
    minute: get('minute'),
  };
};

/**
 * 現在のJSTカレンダー日付を表す JstCalendarDate を返す。
 * 営業日ロジック（4:30境界）は考慮しない純粋なJSTカレンダー日。
 *
 * 文字列で扱いたい場合は `getJstDateString()` を使うこと。
 */
export const getJstToday = (): JstCalendarDate => {
  const { year, month, day } = getJstDateInfo(new Date());
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

/**
 * JSTカレンダー日付を "YYYY-MM-DD" 形式の文字列で返す。
 * APIクエリパラメータやlocalStorage保存など、文字列比較で安全に扱える形式が必要な時に使う。
 *
 * @param date 基準となるDate（省略時は現在時刻）
 * @returns "YYYY-MM-DD" 形式のJSTカレンダー日
 */
export const getJstDateString = (date: Date = new Date()): string => {
  const { year, month, day } = getJstDateInfo(date);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

/**
 * 指定した時刻の営業日を取得する
 * 朝4:30より前の時刻は前日の営業日として扱う
 *
 * @param time - 対象の時刻（Dateオブジェクト）。省略時は現在時刻
 * @returns 営業日（Date、時刻は00:00:00に設定）
 * @example
 * getBusinessDate(new Date('2025-12-22T00:30:00+09:00')) // => 2025-12-21 00:00:00 (local)
 * getBusinessDate(new Date('2025-12-22T10:00:00+09:00')) // => 2025-12-22 00:00:00 (local)
 */
export const getBusinessDate = (time: Date = new Date()): JstCalendarDate => {
  const { year, month, day, hour: jstHour, minute: jstMinute } =
    getJstDateInfo(time);

  // JST のカレンダー日付を基準に local midnight の Date を生成
  const businessDate = new Date(year, month - 1, day, 0, 0, 0, 0);

  const businessStartMinutes = BUSINESS_START_HOUR * 60 + BUSINESS_START_MINUTE;
  const currentMinutes = jstHour * 60 + jstMinute;

  if (currentMinutes < businessStartMinutes) {
    businessDate.setDate(businessDate.getDate() - 1);
  }

  return businessDate;
};

/**
 * 予約選択における基準日を取得
 * spaceTypeに応じて適切な基準日を返す
 *
 * @param spaceType - スペースタイプ
 * @param airport - 空港コード（シャワールームの場合）
 * @param flightType - フライトタイプ（シャワールームの場合）
 * @param terminal - ターミナルコード（シャワールームの場合）
 * @returns 基準日（Date、時刻は00:00:00に設定）
 */
export const getReservationBaseDate = (
  spaceType: (typeof SPACE_TYPE)[keyof typeof SPACE_TYPE],
  airport?: string,
  flightType?: string,
  terminal?: string,
): JstCalendarDate => {
  if (spaceType === SPACE_TYPE.PRIVATE_ROOM) {
    return getPrivateRoomBaseDate();
  }
  return getShowerBaseDate(airport, flightType, terminal);
};

/**
 * 防音個室ブースの基準日を取得
 * 指定された境界時間を過ぎたら、当日は選択不可（翌日開始）とする
 *
 * @returns 基準日（Date、時刻は00:00:00に設定）
 */
export const getPrivateRoomBaseDate = (): JstCalendarDate => {
  const now = new Date();
  const { year, month, day, hour: jstHour } = getJstDateInfo(now);
  const baseDate = new Date(year, month - 1, day, 0, 0, 0, 0);

  if (jstHour >= PRIVATE_ROOM_BASE_DATE_BOUNDARY_HOUR) {
    baseDate.setDate(baseDate.getDate() + 1);
  }

  return baseDate;
};

/**
 * 羽田T3のシャワールームかどうかを判定
 *
 * @param airport - 空港コード
 * @param flightType - フライトタイプ
 * @param terminal - ターミナルコード
 * @returns 羽田T3のシャワールームの場合true
 */
export const isHanedaT3Shower = (
  airport?: string,
  flightType?: string,
  terminal?: string,
): boolean => {
  return (
    airport === AIRPORTS.HANEDA &&
    flightType === FLIGHT_TYPES.INTERNATIONAL &&
    terminal === TERMINALS.TERMINAL3
  );
};

/**
 * シャワールームの基準日を取得
 *
 * @param airport - 空港コード
 * @param flightType - フライトタイプ
 * @param terminal - ターミナルコード
 * @returns 基準日（Date、時刻は00:00:00に設定）
 */
export const getShowerBaseDate = (
  airport?: string,
  flightType?: string,
  terminal?: string,
): JstCalendarDate => {
  if (isHanedaT3Shower(airport, flightType, terminal)) {
    // 羽田T3: 境界時間を過ぎたら翌日を返す
    const now = new Date();
    const { year, month, day, hour: jstHour } = getJstDateInfo(now);
    const baseDate = new Date(year, month - 1, day, 0, 0, 0, 0);

    if (jstHour >= SHOWER_BASE_DATE_BOUNDARY_HOUR) {
      baseDate.setDate(baseDate.getDate() + 1);
    }

    return baseDate;
  }

  // デフォルト: 営業日を使用
  return getBusinessDate();
};
